import { SuiDerivedWallet } from "@aptos-labs/derived-wallet-sui";

/**
 * The Sui account the connected session is bound to.
 *
 * The session's public key is a `SuiDerivedPublicKey`, whose `suiAccountAddress` names the account
 * the wallet will be asked to sign with. `suiWallet.accounts[0]` is only whatever the wallet lists
 * first, which for a multi-account wallet is a different account entirely — reading it produced a
 * funding-source row and a balance for an account the user never connected.
 *
 * `suiAccountAddress` is read structurally rather than through `instanceof SuiDerivedPublicKey`:
 * the lockfile carries two copies of `derived-wallet-sui`, so a class-identity check would return
 * false against a public key the other copy built.
 */
export function resolveSuiAddress(
  // `name` is required so the two parameters cannot be transposed: an account has no `name`, a
  // wallet has no `publicKey`, and a silent `undefined` here degrades a Sui user to Aptos.
  wallet: { name: string; suiWallet?: unknown } | null | undefined,
  account: { publicKey?: unknown } | null | undefined,
): string | undefined {
  if (!(wallet instanceof SuiDerivedWallet)) return undefined;

  const publicKey = account?.publicKey as { suiAccountAddress?: unknown } | undefined;
  if (typeof publicKey?.suiAccountAddress === "string" && publicKey.suiAccountAddress.length > 0) {
    return publicKey.suiAccountAddress;
  }

  // Fallback for the window before `account` resolves, so `chainName` still detects "Sui".
  const suiWallet = wallet.suiWallet as {
    accounts: ReadonlyArray<{ address?: string | undefined }>;
  };
  return suiWallet.accounts[0]?.address ?? undefined;
}
